import React from 'react'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { GeneratedForms } from '../../../types/Templates'
import firebase from 'firebase'
import DateTimeView from '../../atoms/DateTimeView'
import Spacer from '../../atoms/Spacer'
import {
  FormsTableCol,
  FormsTableRow,
} from '../../../pages/CompositeCoursePage/styled'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import theme from '@app/ui/theme'
import { createPortal } from 'react-dom'
import {
  computeMenuFit,
  MenuFit,
  NO_FIT,
  sameFit,
} from '@app/ui/dropdown-menu/menuFit'

type CompositeFinalRatingItemProps = {
  form: GeneratedForms
  showArchiveFormsModal: (formId: string) => void
  onUpdateTitle?: (formId: string, title: string) => Promise<void>
  variant?: 'card' | 'row'
}

export const downloadFolder = (ref: string) => {
  const doc = firebase.storage().ref(ref)
  doc.getDownloadURL().then((url) => {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${document.title || 'Composite_Course_Forms'}.zip`
    console.log({ url, title: document.title })
    anchor.click()
  })
}

const MENU_WIDTH = 200
// The gap the menu used to open with below its button.
const MENU_GAP = 4
// Above the page, below the modals the menu opens.
const MENU_Z_INDEX = 9000

const KebabMenu: React.FC<{
  form: GeneratedForms
  showArchiveFormsModal: (formId: string) => void
  onUpdateTitle?: (formId: string, title: string) => Promise<void>
}> = ({ form, showArchiveFormsModal, onUpdateTitle }) => {
  const [open, setOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [titleValue, setTitleValue] = React.useState(form.title ?? '')
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)
  // The card clips whatever leaves it, so the menu is portalled out to <body> and
  // carries its own position: under its button, corrected to stay on screen.
  const [anchor, setAnchor] = React.useState<{ top: number; left: number }>()
  const [fit, setFit] = React.useState<MenuFit>(NO_FIT)
  const fitRef = React.useRef<MenuFit>(NO_FIT)

  const measure = React.useCallback(() => {
    const trigger = triggerRef.current
    const menu = menuRef.current
    if (!trigger || !menu) return

    const triggerRect = trigger.getBoundingClientRect()
    // Right-aligned to the button, the way the menu sat before it was portalled.
    const next = {
      top: triggerRect.bottom + MENU_GAP,
      left: triggerRect.right - MENU_WIDTH,
    }
    setAnchor((prev) =>
      prev && prev.top === next.top && prev.left === next.left ? prev : next
    )

    const applied = fitRef.current
    const menuRect = menu.getBoundingClientRect()
    const nextFit = computeMenuFit({
      // Where the menu wants to sit, before the correction we are about to apply.
      menu: {
        left: next.left,
        top: next.top,
        bottom: next.top + menuRect.height,
        width: menuRect.width,
      },
      trigger: triggerRect,
      contentHeight: applied.maxHeight ? menu.scrollHeight : menuRect.height,
      appliedMaxHeight: applied.maxHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    })
    if (sameFit(fitRef.current, nextFit)) return
    fitRef.current = nextFit
    setFit(nextFit)
  }, [])

  React.useLayoutEffect(() => {
    if (!open) {
      fitRef.current = NO_FIT
      setFit(NO_FIT)
      setAnchor(undefined)
      return
    }

    measure()

    // The row moves under the menu when the page scrolls, and the menu changes
    // height when the title field opens inside it.
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => measure())
        : null
    if (menuRef.current) observer?.observe(menuRef.current)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, measure])

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return
      }
      setOpen(false)
      setEditing(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleSave = async () => {
    await onUpdateTitle?.(form.id, titleValue)
    setEditing(false)
    setOpen(false)
  }

  const menu = (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: (anchor?.top ?? 0) + fit.dy,
        left: (anchor?.left ?? 0) + fit.dx,
        // Hidden for the frame it takes to measure where it fits.
        visibility: anchor ? 'visible' : 'hidden',
        maxHeight: fit.maxHeight ?? undefined,
        overflowY: fit.maxHeight ? 'auto' : undefined,
        background: '#fff',
        border: '1px solid #ddd',
        borderRadius: 8,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        zIndex: MENU_Z_INDEX,
        width: MENU_WIDTH,
        boxSizing: 'border-box',
        padding: '6px 0',
      }}
    >
      {editing ? (
        <div style={{ padding: '6px 8px', display: 'flex', gap: 6 }}>
          <input
            autoFocus
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave()
              if (e.key === 'Escape') {
                setEditing(false)
                setTitleValue(form.title ?? '')
              }
            }}
            style={{
              flex: 1,
              minWidth: 0,
              border: '1px solid #ccc',
              borderRadius: 5,
              padding: '0 12px',
              fontSize: 14,
              height: 40,
              boxSizing: 'border-box',
            }}
          />
          <button
            onClick={handleSave}
            style={{
              background: '#151A20',
              border: 'none',
              borderRadius: 5,
              cursor: 'pointer',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FeatherIcon name="check" size={16} color="#fff" />
          </button>
          <button
            onClick={() => {
              setEditing(false)
              setTitleValue(form.title ?? '')
            }}
            style={{
              background: 'none',
              border: '1px solid #ccc',
              borderRadius: 5,
              cursor: 'pointer',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FeatherIcon name="x" size={16} color="#333" />
          </button>
        </div>
      ) : (
        <MenuItem
          icon="edit-2"
          label="Edit title"
          onClick={() => setEditing(true)}
        />
      )}
      <div style={{ borderTop: '1px solid #eee', margin: '4px 0' }} />
      <MenuItem
        icon="trash-2"
        label="Delete"
        color="#d35"
        onClick={() => {
          showArchiveFormsModal(form.id)
          setOpen(false)
        }}
      />
    </div>
  )

  return (
    <div ref={triggerRef} style={{ display: 'inline-block' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'none',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          padding: '4px 8px',
          display: 'flex',
          alignItems: 'center',
          height: 40,
          boxSizing: 'border-box',
        }}
      >
        <FeatherIcon name="more-vertical" size={16} color="#333" />
      </button>
      {open && createPortal(menu, document.body)}
    </div>
  )
}

const MenuItem: React.FC<{
  icon: string
  label: string
  color?: string
  onClick: () => void
}> = ({ icon, label, color = '#333', onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      width: '100%',
      height: 40,
      padding: '0 18px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: 14,
      color,
      textAlign: 'left',
      boxSizing: 'border-box',
    }}
    onMouseEnter={(e) =>
      ((e.currentTarget as HTMLButtonElement).style.background = '#f5f5f5')
    }
    onMouseLeave={(e) =>
      ((e.currentTarget as HTMLButtonElement).style.background = 'none')
    }
  >
    <FeatherIcon name={icon} size={16} color={color} />
    {label}
  </button>
)

const CompositeFinalRatingItem: React.FC<CompositeFinalRatingItemProps> = ({
  form,
  showArchiveFormsModal,
  onUpdateTitle,
  variant = 'card',
}) => {
  if (variant === 'row') {
    return (
      <FormsTableRow>
        <FormsTableCol>{form.title ?? '-'}</FormsTableCol>
        <FormsTableCol>{form.name ?? '-'}</FormsTableCol>
        <FormsTableCol>{form.courseName ?? '-'}</FormsTableCol>
        <FormsTableCol>
          <DateTimeView date={form.ratingDate} showTime={false} />
        </FormsTableCol>
        <FormsTableCol>
          <DateTimeView
            date={form.createdAt ? new Date(form.createdAt) : new Date()}
          />
        </FormsTableCol>
        <FormsTableCol style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <KebabMenu
              form={form}
              showArchiveFormsModal={showArchiveFormsModal}
              onUpdateTitle={onUpdateTitle}
            />
            <RoundedButton
              title="DOWNLOAD FORMS"
              onPress={() => downloadFolder(form.zipPath)}
              style={{ backgroundColor: theme.colors.primary2 }}
            />
          </div>
        </FormsTableCol>
      </FormsTableRow>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h4 style={{ marginBlockEnd: '0.5em' }}>Composite Forms Details</h4>
        <KebabMenu
          form={form}
          showArchiveFormsModal={showArchiveFormsModal}
          onUpdateTitle={onUpdateTitle}
        />
      </div>
      <p style={{ marginBlock: '0.5em' }}>
        Generated:{' '}
        <DateTimeView
          date={form.createdAt ? new Date(form.createdAt) : new Date()}
        />
      </p>
      <p>Title: {form.title}</p>
      <Spacer height={4} />

      <p>Rating Name: {form?.name}</p>
      <p>
        Rating Date:{' '}
        <DateTimeView date={form.ratingDate} showTime={false} />
      </p>
      <p>Club Name: {form?.clubName}</p>
      <p>Course Name: {form?.courseName}</p>
      <div style={{ marginBlockEnd: '2em' }}>
        <RoundedButton
          title="Download Forms"
          onPress={() => downloadFolder(form.zipPath)}
        />
      </div>
    </div>
  )
}
export default CompositeFinalRatingItem
